import React from 'react'
import { useNavigate } from 'react-router-dom'

import Loading from '../../../components/layout/Loading'
import { useI18N } from '../../../../libraries/i18n/hook'
import AuthorsLabel from '../../../components/content/AuthorsLabel'
import { MetadataLabel, VideoCard } from '../../../components/content'
import * as dateLib from '../../../../libraries/date'
import ContentTag from '../../../components/content/ContentTag'
import PageHeading from '../../../components/layout/PageHeading'
import FullVideoPlayer from '../../../components/video/fullPlayer'
import VideosCarousel from './Carousel'
import PageSection from '../../../components/layout/PageSection'
import { useMutateVideoActivity, useRelatedVideos, useVideoById } from '../../../../hocs/content/useVideos'
import config from '../../../../config'
import BookmarkVideoButton from './BookmarkButton'
import DescriptionText from '../../../components/content/DescriptionText'
import ShareButton from '../../../components/form/ShareButton'
import MoreActionsButton from '../../../components/form/MoreActionsButton'
import Button from '../../../components/form/Button'
import { Video } from '../../../../api/videos/types'

type Props = { id: string, redirectToNextVideo: () => void, nextVideo?: Video }
const Details: React.FC<Props> = ({ id, redirectToNextVideo, nextVideo }) => {
  const { t } = useI18N()
  const { data, isLoading, error } = useVideoById(id)
  const { trigger: submitVideoActivity } = useMutateVideoActivity(id)
  const [videoEnded, setVideoEnded] = React.useState(false)
  const [secondsToPlayNextVideo, setSecondsToPlayNextVideo] = React.useState(5)
  const navigate = useNavigate()

  // Countdown effect when video ends
  React.useEffect(() => {
    if (videoEnded && secondsToPlayNextVideo > 0) {
      const timer = setTimeout(() => {
        setSecondsToPlayNextVideo(prev => prev - 1)
      }, 1000)

      return () => clearTimeout(timer)
    }

    // Redirect when countdown reaches 0
    if (videoEnded && secondsToPlayNextVideo === 0) {
      redirectToNextVideo()
    }
  }, [videoEnded, secondsToPlayNextVideo, navigate])

  if(isLoading) return <Loading size='fill-container' />
  if(error) return <div>{t(`error.${error?.code}`)}</div>
  if(!data) return <div>This should not happen</div>

  const handleVideoPageClosed = (playTime: number) => {
    if(playTime) {
      submitVideoActivity({ body: { currentPlayTime: playTime }, videoId: id })
    }
  }

  const handleVideoEnded = () => {
    submitVideoActivity({ body: { currentPlayTime: 0, watched: true }, videoId: id })
    setVideoEnded(true)
  }

  const handleCancelNextVideo = () => {
    setVideoEnded(false)
  }

  return (
    <div>
      <PageHeading>{data.title}</PageHeading>
      <DescriptionText>{data.details}</DescriptionText>
      <div className='w-full md:w-4/5 m-auto my-8'>
        {videoEnded && nextVideo && (
          <div style={{ height: '613px' }} className='w-full bg-deep-green flex items-center justify-center'>
            <div className='w-[270px] text-center'>
              <VideoCard
                id={nextVideo.id}
                title={nextVideo.title}
                image={nextVideo.thumbnailsUrls.desktop}
                previewUrl={nextVideo.previewUrl}
                authors={nextVideo.authors}
                duration={nextVideo.watchTime}
                viewersCount={nextVideo.viewersCount}
                type={nextVideo.type}
                difficulty={nextVideo.difficultyLevel.name}
                category={nextVideo.mainCategory.name}
                currentPlayTime={nextVideo.userActivity?.currentPlayTime}
                watched={nextVideo.userActivity?.watched}
                bookmarked={nextVideo.userActivity?.saved}
              />
              {/* Countdown to play next video */}
              <div className='mt-4 text-white'>
                <div className='text-lg font-semibold mb-2'>{t('videos.next.playing.in')}</div>
                <div className='text-3xl font-bold mb-4'>{secondsToPlayNextVideo}</div>
                <Button size='sm' outlined variant='tertiary' onClick={handleCancelNextVideo}>{t('common.cancel')}</Button>
              </div>
            </div>
          </div>
        )}
        {(!videoEnded || !nextVideo) && data && data.srcUrl && (
          <FullVideoPlayer
            videoId={data.id}
            srcUrl={data.srcUrl}
            onClose={handleVideoPageClosed}
            playTime={data.userActivity?.currentPlayTime}
            handleVideoEnded={handleVideoEnded}
          />
        )}
        <div className='my-4 flex flex-col-reverse md:flex-row md:flex justify-between'>
          <div className='flex flex-col-reverse mt-4 md:mt-0 block md:flex-row md:items-center gap-x-6 text-deep-green text-sm'>
            <AuthorsLabel authors={data.authors} strokeColor='deep-green' />
            <div className='flex gap-x-5 mb-2 md:mb-0'>
              <MetadataLabel icon='clock' strokeColor='deep-green'><div className='text-2xs sm:text-xs'>{dateLib.parseSecondsToHoursAndMinutes(data.watchTime)}</div></MetadataLabel>
              <MetadataLabel icon='play' strokeColor='deep-green'><div className='text-2xs sm:text-xs'>{data.viewersCount}</div></MetadataLabel>
              <MetadataLabel icon='calendar' strokeColor='deep-green'><div className='text-2xs sm:text-xs'>{dateLib.parseToShortDate(data.createdAt)}</div></MetadataLabel>
            </div>
          </div>
          <div className='flex items-center gap-x-6'>
            <ContentTag label={data.mainCategory.name} bgColor='white' textColor='deep-green' />
            <ContentTag label={data.difficultyLevel.name} bgColor='deep-green' />
            <div className='hidden sm:inline-block'><BookmarkVideoButton id={data.id} bookmarked={data.userActivity?.saved} /></div>
            <div className='hidden sm:inline-block'><ShareButton title={data.title} /></div>
            <div className='ml-auto inline-block sm:hidden'>
              <MoreActionsButton
                items={[
                  { id: 'bookmark', item: <BookmarkVideoButton id={data.id} bookmarked={data.userActivity?.saved}>{data.userActivity?.saved ? t('content.bookmarked.remove') : t('content.bookmarked.add')}</BookmarkVideoButton> },
                  { id: 'share', item: <ShareButton title={data.title} size='md' /> }
                ]}
              />
            </div>
          </div>
        </div>
      </div>
      <PageSection title={t('content.related')}>
        <VideosCarousel queryFn={() => useRelatedVideos(data.id, config.VITE_MAX_CAROUSEL_ITEMS)}  />
      </PageSection>
    </div>
  )
}

export default Details
